import type {
  ChannexParentPublicationState,
  ChannexPublicationState,
  ChannexUnitPublicationRow,
  PropertyLifecycleStatus,
} from '@/shared/api';

export interface UnitPresentation {
  label: string;
  tone: 'neutral' | 'progress' | 'ok' | 'warn' | 'error';
  hint?: string;
}

const UNITS: Record<ChannexPublicationState, UnitPresentation> = {
  LocalOnly: { label: 'Nur lokal', tone: 'neutral' },
  Publishing: {
    label: 'Wird veröffentlicht',
    tone: 'progress',
    hint: 'Läuft gerade — ein zweiter Anlauf würde nichts beschleunigen.',
  },
  Published: { label: 'Veröffentlicht', tone: 'ok' },
  PendingChanges: {
    label: 'Änderungen offen',
    tone: 'warn',
    hint: 'Beim Kanalmanager angelegt, aber eine neuere Fassung ist liegengeblieben.',
  },
  Failed: { label: 'Fehler', tone: 'error' },
  // Ein mehrdeutiger Vendor-Create darf nie per Klick wiederholt werden — die Zeile sagt, dass
  // Hilfe nötig ist, und bietet bewusst keinen Knopf dafür an.
  NeedsReconcile: {
    label: 'Manuelle Klärung nötig',
    tone: 'error',
    hint: 'Der Kanalmanager hat einen mehrdeutigen Zustand gemeldet. Das löst der Betreiber auf, nicht ein erneuter Versuch.',
  },
};

/** Ein unbekannter Zustand darf eine Zeile eintrüben, nicht die ganze Fläche leeren. */
const UNKNOWN: UnitPresentation = { label: 'Unbekannt', tone: 'neutral' };

export function presentUnit(state: ChannexPublicationState): UnitPresentation {
  return UNITS[state] ?? UNKNOWN;
}

/**
 * Auswählbar ist, was der **Server** als veränderbar ausweist. Die Oberfläche leitet das nicht
 * mehr selbst aus dem Zustand ab: nur der Schreibpfad weiß, was er annimmt — ein archiviertes
 * Apartment weist er ab, und zwar samt der ganzen Auswahl.
 */
export function selectableUnits(units: ChannexUnitPublicationRow[]): string[] {
  return units.filter((unit) => unit.canPublish).map((unit) => unit.propertyId);
}

/**
 * Die Auswahl, die wirklich abgeschickt wird. Zwischen Anklicken und Absenden kann sich der
 * Serverzustand verschoben haben (die Fläche pollt, während etwas läuft); eine Zeile, die nicht
 * mehr veränderbar ist, wird ungehakt gezeigt — dann darf ihre Id auch nicht mitfahren, sonst
 * lehnt der Server die **ganze** Auswahl ab und die anderen Apartments bleiben liegen.
 */
export function effectiveSelection(
  selected: string[],
  units: ChannexUnitPublicationRow[],
): string[] {
  const selectable = new Set(selectableUnits(units));
  return selected.filter((id) => selectable.has(id));
}

/** Alles Offene gewählt? Über die Mitglieder, nicht über die Anzahl — die kann zufällig passen. */
export function allSelectableChosen(selected: string[], selectable: string[]): boolean {
  const chosen = new Set(selected);
  return selectable.length > 0 && selectable.every((id) => chosen.has(id));
}

/** Läuft gerade etwas? Dann lohnt sich Nachfragen, sonst wäre die Fläche eingefroren. */
export function isSettling(
  parentState: ChannexParentPublicationState,
  units: ChannexUnitPublicationRow[],
): boolean {
  return parentState === 'Publishing' || units.some((unit) => unit.state === 'Publishing');
}

/** Pausieren ist die kleine Schwester des Archivierens — sie wirkt auf dieselbe Verteilung. */
export type LifecycleAction = 'pause' | 'resume' | null;

export function lifecycleAction(status: PropertyLifecycleStatus): LifecycleAction {
  if (status === 'Active') return 'pause';
  if (status === 'Paused') return 'resume';
  return null;
}

/**
 * Was der Hauptknopf gerade tut. Ohne Parent ist das Anlegen des Objekts dran — Units ohne ihn
 * hätten nichts, woran sie hängen könnten.
 */
export type PublishAction = 'parent' | 'units' | 'none';

export function nextAction(
  parentState: ChannexParentPublicationState,
  selectedCount: number,
): PublishAction {
  if (parentState === 'NotPublished' || parentState === 'Failed') return 'parent';
  if (parentState !== 'Published') return 'none';
  return selectedCount > 0 ? 'units' : 'none';
}
